/**
 * PageSpeedService.js
 * This service handles PageSpeed Insights analysis requests and responses.
 */
import PageSpeedInsightsService from './PageSpeedInsightsService';
import TechnologyDetectionService from './TechnologyDetectionService';

// Create PageSpeedService instance
class PageSpeedService {
  /**
   * Initialize the PageSpeed service
   */
  constructor() {
    // Use the environment variable for the API base URL
    const apiPort = process.env.REACT_APP_API_PORT || '5002';
    this.apiBaseUrl = process.env.REACT_APP_API_URL || `http://localhost:${apiPort}/api`;
    this.psiService = PageSpeedInsightsService;
    this.inFlightRequests = new Map(); // Track in-flight requests
    this.technologyDetectionService = TechnologyDetectionService;
  }

  /**
   * Analyze a URL using PageSpeed Insights
   * @param {string} url - The URL to analyze
   * @param {Object} options - Analysis options
   * @returns {Promise<Object>} - Analysis results
   */
  async analyzeUrl(url, options = {}) {
    try {
      const defaultOptions = {
        mobile: false,
        category: 'performance',
        localOnly: false // Set localOnly to false by default so it saves to server
      };

      const mergedOptions = { ...defaultOptions, ...options };
      
      // Handle category parameter - ensure it's properly formatted for API call
      if (Array.isArray(mergedOptions.category)) {
        // Join array into comma-separated string for API
        mergedOptions.categoryString = mergedOptions.category.join(',');
      } else {
        // Use single category as string
        mergedOptions.categoryString = mergedOptions.category;
      }
      
      console.log('PageSpeed analysis with categories:', mergedOptions.categoryString);
      
      // Create a request key to detect duplicates
      const requestKey = `${url}-${mergedOptions.mobile}`;
      
      // Check if we already have this request in flight
      if (this.inFlightRequests.has(requestKey)) {
        console.log(`Duplicate request detected for ${url}. Reusing existing promise.`);
        return this.inFlightRequests.get(requestKey);
      }
      
      // Create a new promise for this request
      const analysisPromise = (async () => {
        try {
          // Call the PageSpeed Insights API directly from the client
          const psiData = await this.psiService.analyzeUrl(url, {
            mobile: mergedOptions.mobile,
            category: mergedOptions.categoryString // Use properly formatted category string
          });

          // Transform the PSI response into our format
          const { metrics, response } = this.psiService.transformResults(psiData, url, mergedOptions);

          // Ensure rawData is preserved from the original response
          if (!response.rawData && psiData) {
            response.rawData = psiData;
          }
          
          // Store optional values
          response.url = url;
          response.mobile = mergedOptions.mobile;
          response.timestamp = new Date().toISOString();
          
          // Normalize the raw data structure to ensure consistent format
          if (response.rawData) {
            console.log('Normalizing raw data structure...');
            response.rawData = this._normalizeRawData(response.rawData);
          }
          
          // Create the result object
          const result = { metrics, response };
          
          // Return result without saving - saving will be handled after all checks are complete
          return result;
        } catch (error) {
          console.error('Analysis error:', error);
          throw error;
        } finally {
          // Clean up the in-flight request when done
          this.inFlightRequests.delete(requestKey);
        }
      })();
      
      // Store the promise to prevent duplicate requests
      this.inFlightRequests.set(requestKey, analysisPromise);
      
      return analysisPromise;
    } catch (error) {
      console.error('Error in analyzeUrl:', error);
      throw new Error(`Failed to analyze URL: ${error.message}`);
    }
  }

  // Trim down the analysis data to save bandwidth and storage
  trimAnalysisData(response) {
    if (!response) return null;
    
    // Create a simplified version with only the data we need
    const trimmed = {
      url: response.url,
      mobile: response.mobile,
      timestamp: response.timestamp,
      performance: response.categories?.performance?.score || 0,
      metrics: response.audits ? Object.keys(response.audits)
        .filter(key => response.audits[key].numericValue)
        .reduce((acc, key) => {
          acc[key] = {
            score: response.audits[key].score,
            numericValue: response.audits[key].numericValue,
            displayValue: response.audits[key].displayValue
          };
          return acc;
        }, {}) : {}
    };
    
    return trimmed;
  }

  async analyzeBatch(urlList, options = {}) {
    if (!Array.isArray(urlList) || urlList.length === 0) {
      throw new Error('URL list must be a non-empty array');
    }
    
    const results = [];
    const errors = [];
    
    // Process URLs in sequence to prevent rate limiting
    for (const url of urlList) {
      try {
        const result = await this.analyzeUrl(url, options);
        results.push({
          url,
          success: true,
          data: result
        });
      } catch (error) {
        console.error(`Failed to analyze ${url}:`, error);
        errors.push({
          url,
          success: false,
          error: error.message || 'Unknown error'
        });
        results.push({
          url,
          success: false,
          error: error.message || 'Unknown error'
        });
      }
    }
    
    return {
      results,
      errors,
      totalProcessed: urlList.length,
      successCount: results.filter(r => r.success).length,
      errorCount: errors.length
    };
  }

  /**
   * Save PageSpeed analysis results to the server
   * @param {object} analysisData - PageSpeed analysis data
   * @returns {Promise<object>} - Server response
   */
  async saveAnalysis(analysisData) {
    try {
      console.log(`Saving analysis for URL: ${analysisData.url}`);
      
      // Create URL hash for deduplication
      const urlHash = await this._createUrlHash(analysisData.url);

      console.log('URL hash:', urlHash);
      
      // Map analysis data to match the new table structure
      const serverData = {
        url: analysisData.url,
        url_hash: urlHash,
        is_mobile: Boolean(analysisData.mobile),
        browser: navigator.userAgent.split(' ').pop(),
        connectivity: 'cable', // Default to cable as we don't have this info from client
        load_time: analysisData.metrics?.timings?.speedIndex || null,
        page_load_time: analysisData.metrics?.timings?.onLoad || null,
        dom_complete: analysisData.metrics?.timings?.domContentLoaded || null,
        first_paint: analysisData.metrics?.timings?.firstContentfulPaint || null,
        transfer_size: analysisData.metrics?.timings?.timeToFirstByte || null,
        requests: analysisData.metrics?.timings?.totalBlockingTime || null,
        cpu_load_percent: null, // Client doesn't have access to this
        memory_usage_mb: null, // Client doesn't have access to this
        raw_metrics: JSON.stringify(analysisData.metrics || {}),
        raw_data: JSON.stringify(analysisData.rawData || {}),
        performance_score: analysisData.metrics?.performance?.score || 0,
        error_message: analysisData.error || null,
        report_id: this._generateReportId(),
        status: 'completed',
        report_url: null, // Will be set by server
        report_hash: null, // Will be set by server
        report_generated_at: new Date().toISOString(),
        report_accessed_at: new Date().toISOString(),
        report_access_count: 0,
        // Make sure meta_data is stringified
        meta_data: JSON.stringify({
          client: 'web-client',
          version: '1.0.0',
          analyzedAt: new Date().toISOString()
        })
      };
      
      // Send to server API
      const response = await fetch(`${this.apiBaseUrl}/pagespeed/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : ''
        },
        body: JSON.stringify(serverData)
      });
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Analysis saved successfully:', result);
      
      return result;
    } catch (error) {
      console.error('Error saving analysis:', error);
      throw error;
    }
  }

  /**
   * Generate a unique report ID
   * @private
   * @returns {string} - Unique report ID
   */
  _generateReportId() {
    return 'PSI-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Create a hash of the URL + timestamp for deduplication
   * @private
   * @param {string} url - URL to hash
   * @returns {string} - Unique URL hash
   */
  async _createUrlHash(url) {
    const timestamp = new Date().toISOString();
    const uniqueString = `${timestamp}_${url.toLowerCase()}`;
    const encoder = new TextEncoder();
    const data = encoder.encode(uniqueString);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  // More aggressive trimming for sending to server
  aggressiveTrim(data) {
    if (!data) return null;
    
    console.log('Trimming data for server. Original data structure:', 
      Object.keys(data).join(', '));
    
    if (data.rawData) {
      console.log('Raw data available. Raw data structure:', 
        Object.keys(data.rawData).join(', '));
    }
    
    // Ensure we have at least some data structure for metrics
    const fallbackMetrics = {
      'first-contentful-paint': 0,
      'speed-index': 0,
      'largest-contentful-paint': 0,
      'interactive': 0,
      'total-blocking-time': 0,
      'cumulative-layout-shift': 0
    };
    
    // Extract only essential information for the top-level object
    const result = {
      url: data.url,
      mobile: data.mobile,
      timestamp: data.timestamp || new Date().toISOString(),
      // Try to get performance score from several possible locations
      performanceScore: data.categories?.performance?.score || 
                        data.rawData?.categories?.performance?.score || 
                        data.performanceScore || 
                        0,
      // First try direct audits, then rawData audits
      metrics: {
        'first-contentful-paint': data.audits?.['first-contentful-paint']?.numericValue || 
                                 data.rawData?.audits?.['first-contentful-paint']?.numericValue || 
                                 data.metrics?.['first-contentful-paint'] ||
                                 fallbackMetrics['first-contentful-paint'],
        
        'speed-index': data.audits?.['speed-index']?.numericValue || 
                       data.rawData?.audits?.['speed-index']?.numericValue || 
                       data.metrics?.['speed-index'] ||
                       fallbackMetrics['speed-index'],
        
        'largest-contentful-paint': data.audits?.['largest-contentful-paint']?.numericValue || 
                                   data.rawData?.audits?.['largest-contentful-paint']?.numericValue || 
                                   data.metrics?.['largest-contentful-paint'] ||
                                   fallbackMetrics['largest-contentful-paint'],
        
        'interactive': data.audits?.['interactive']?.numericValue || 
                      data.rawData?.audits?.['interactive']?.numericValue || 
                      data.metrics?.['interactive'] ||
                      fallbackMetrics['interactive'],
        
        'total-blocking-time': data.audits?.['total-blocking-time']?.numericValue || 
                              data.rawData?.audits?.['total-blocking-time']?.numericValue || 
                              data.metrics?.['total-blocking-time'] ||
                              fallbackMetrics['total-blocking-time'],
        
        'cumulative-layout-shift': data.audits?.['cumulative-layout-shift']?.numericValue || 
                                  data.rawData?.audits?.['cumulative-layout-shift']?.numericValue || 
                                  data.metrics?.['cumulative-layout-shift'] ||
                                  fallbackMetrics['cumulative-layout-shift']
      },
      // Include metadata
      lighthouseVersion: data.lighthouseVersion || data.rawData?.lighthouseVersion || 'unknown',
      userAgent: data.userAgent || data.rawData?.userAgent || navigator.userAgent,
      environment: data.environment || {
        hostUserAgent: navigator.userAgent,
        networkUserAgent: navigator.userAgent,
        benchmarkIndex: 1000
      },
      // Any custom metadata
      metaData: {
        client: 'web-client',
        version: '1.0.0',
        analyzedAt: new Date().toISOString()
      }
    };
    
    // Store the full raw data for saving in the database
    // Prioritize data.rawData if available, otherwise use the original data
    let rawData = null;
    if (data.rawData) {
      rawData = this._reduceRawDataSize(data.rawData);
    } else if (data.categories || data.audits) {
      // If we have direct lighthouse data at the top level
      rawData = this._reduceRawDataSize(data);
    }
    
    if (rawData) {
      result.fullRawData = rawData;
      
      // Estimate the JSON size
      const estimatedSize = JSON.stringify(result).length;
      console.log('Estimated data size:', Math.round(estimatedSize / 1024), 'KB');
      
      // If still too large, further reduce by removing all screenshots
      if (estimatedSize > 800000) { // If close to 1MB limit
        console.log('Data still too large, removing screenshot data');
        if (result.fullRawData.audits) {
          // Remove screenshot details which are very large
          Object.keys(result.fullRawData.audits).forEach(key => {
            const audit = result.fullRawData.audits[key];
            if (audit && audit.details && audit.details.screenshots) {
              delete audit.details.screenshots;
            }
            if (audit && audit.details && audit.details.items) {
              // Remove base64 data from items
              if (Array.isArray(audit.details.items)) {
                audit.details.items = audit.details.items.map(item => {
                  if (item.snapshot) delete item.snapshot;
                  return item;
                });
              }
            }
          });
        }
      }
    }
    
    console.log('Trimmed metrics:', JSON.stringify(result.metrics, null, 2));
    console.log('Full raw data included:', !!result.fullRawData);
    
    return result;
  }
  
  // Helper method to reduce the size of raw data
  _reduceRawDataSize(rawData) {
    // Clone to avoid modifying the original
    const reduced = JSON.parse(JSON.stringify(rawData));
    
    // Common large fields to remove or reduce
    if (reduced.audits) {
      // Remove screenshot data which is very large
      Object.keys(reduced.audits).forEach(key => {
        const audit = reduced.audits[key];
        // Remove full page screenshot which is the largest
        if (key === 'full-page-screenshot') {
          delete reduced.audits[key];
        }
        // Remove other screenshot collections
        else if (audit && audit.details && audit.details.screenshot) {
          delete audit.details.screenshot;
        }
      });
    }
    
    return reduced;
  }

  async getHistory() {
    // Return mock data for demo
    return {
      success: true,
      history: [],
      count: 0
    };
  }

  async getAnalysisResult(id) {
    // Return mock result for demo
    return {
      success: false,
      message: 'Analysis not found'
    };
  }

  /**
   * Process and normalize the raw Lighthouse data to ensure it has the correct structure
   * @private
   * @param {Object} rawData - The raw Lighthouse data
   * @returns {Object} - Normalized data
   */
  _normalizeRawData(rawData) {
    if (!rawData) return null;
    
    // If it's a string, try to parse it
    if (typeof rawData === 'string') {
      try {
        rawData = JSON.parse(rawData);
      } catch (e) {
        console.error('Error parsing raw data:', e);
        return { error: 'Failed to parse raw data' };
      }
    }
    
    // Check if rawData already has the correct structure
    if (rawData.lighthouseResult) {
      console.log('Raw data already has lighthouseResult structure');
      return rawData;
    }
    
    // If rawData contains audits and categories directly, nest it properly
    if (rawData.audits && rawData.categories) {
      console.log('Nesting raw data in lighthouseResult structure');
      return { lighthouseResult: rawData };
    }
    
    // If raw_data field exists and is a string, try to parse that
    if (rawData.raw_data && typeof rawData.raw_data === 'string') {
      try {
        const parsedRawData = JSON.parse(rawData.raw_data);
        console.log('Successfully parsed raw_data field');
        return this._normalizeRawData(parsedRawData); // Recursively check the structure
      } catch (e) {
        console.error('Error parsing raw_data field:', e);
      }
    }
    
    // Return the original data if no transformations were possible
    return rawData;
  }
}

// Create a single instance of the service
const pageSpeedService = new PageSpeedService();

// Export the instance
export default pageSpeedService;